import React from 'react'
import { Pressable, View } from 'react-native'

import CloseIcon from '@common/assets/svg/CloseIcon'
import BottomSheet from '@common/components/BottomSheet'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import flexbox from '@common/styles/utils/flexbox'
import { getUiType } from '@web/utils/uiType'

const { isTab } = getUiType()

// Same cap as PriceImpactWarningModal: a centered dialog on wide viewports, a
// bottom sheet in the 360px-wide popup.
const SHEET_WIDTH_STYLE = isTab
  ? { width: 520, maxWidth: '92%' as const, alignSelf: 'center' as const }
  : undefined

type Props = {
  id: string
  sheetRef: React.RefObject<any>
  title?: string
  /** Omit to hide the close button (e.g. while a transaction is in flight). */
  onClose?: () => void
  onClosed?: () => void
  /** Open as soon as the sheet mounts (its ref is not set yet in a parent effect). */
  autoOpen?: boolean
  /**
   * `status`: the create / cancel progress. Same surface as the Swap & Bridge
   * status card (TrackProgressCard) and no header: its buttons close it.
   */
  variant?: 'default' | 'status'
  /**
   * Hide at once, without the close animation. Mobile-web and the extension popup
   * close the sheet while a step is signed in SignAccountOpOverlay (see
   * SIGNS_IN_OVERLAY); that mount stalls the slide-out, so the
   * sheet would hang over the sign screen after its backdrop had already faded.
   */
  hidden?: boolean
  children: React.ReactNode
}

// The active rail node's pulse ring grows past the node (28px × 1.9).
export const STATUS_PULSE_ROOM = 14

const STATUS_WIDTH_STYLE = isTab
  ? { width: 480, maxWidth: '92%' as const, alignSelf: 'center' as const }
  : undefined

/** Dialog chrome shared by the DCA Review / progress / slippage modals. */
const DcaModalShell = ({
  id,
  sheetRef,
  title,
  onClose,
  onClosed,
  autoOpen,
  variant = 'default',
  hidden,
  children
}: Props) => {
  const { theme } = useTheme()
  const isStatus = variant === 'status'

  return (
    <BottomSheet
      id={id}
      sheetRef={sheetRef}
      closeBottomSheet={onClose}
      onClosed={onClosed}
      autoOpen={autoOpen}
      type={isTab ? 'modal' : 'bottom-sheet'}
      style={{
        ...(isStatus
          ? {
              backgroundColor: theme.onboardingCardBackground,
              // Longhands: the sheet / modal styles set per-corner radii, which
              // win over a `borderRadius` shorthand on react-native-web.
              borderTopStartRadius: 24,
              borderTopEndRadius: 24,
              borderBottomStartRadius: 24,
              borderBottomEndRadius: 24,
              paddingHorizontal: isTab ? 32 : 16,
              // Less on top: DcaFlowStatus reserves room there for the rail's
              // pulse ring, which the sheet's scroll view would clip.
              paddingTop: isTab ? 40 - STATUS_PULSE_ROOM : 24 - STATUS_PULSE_ROOM,
              paddingBottom: isTab ? 40 : 16,
              ...STATUS_WIDTH_STYLE
            }
          : {
              backgroundColor: theme.primaryBackground,
              paddingHorizontal: 16,
              paddingVertical: 16,
              ...SHEET_WIDTH_STYLE
            }),
        ...(hidden ? { opacity: 0, pointerEvents: 'none' as const } : {})
      }}
      withBackdropBlur={false}
      shouldBeClosableOnDrag={false}
    >
      {!isStatus && (
        <View
          style={[flexbox.directionRow, flexbox.alignCenter, { minHeight: 36, marginBottom: 8 }]}
        >
          <View style={{ width: 36 }} />
          <Text fontSize={16} weight="semiBold" style={{ flex: 1, textAlign: 'center' }}>
            {title || ''}
          </Text>
          <View style={{ width: 36, alignItems: 'flex-end' }}>
            {!!onClose && (
              <Pressable
                onPress={onClose}
                testID={`${id}-close`}
                style={({ hovered }: any) => [
                  flexbox.center,
                  {
                    width: 32,
                    height: 32,
                    borderRadius: 16,
                    backgroundColor: hovered
                      ? (theme.quaternaryBackground as string)
                      : (theme.secondaryBackground as string)
                  }
                ]}
              >
                <CloseIcon width={12} height={12} color={theme.iconPrimary} />
              </Pressable>
            )}
          </View>
        </View>
      )}
      {children}
    </BottomSheet>
  )
}

export default React.memo(DcaModalShell)
